import { PROMOTION_TYPES, VOUCHER_EXTRA_TYPE } from '@/domainLogic/index.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import LastMinuteDealsBadge from '@/app/common/components/Promotions/LastMinuteDealsBadge';
import { PERIODIC_PROMOTION_LABEL_KEYS } from '@/app/common/periodicPromotions.ts';
import type { PromotionConfig } from '@/app/components/search/components/Card/types.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { replacePlaceholdersWithValues } from '@/app/components/search/utils/translations.ts';
import { getVoucherExtraPromotionValues } from '@/app/components/search/components/Card/utils/getPromotionsUtils.ts';
import { voucherExtraBannerSupported } from '@/app/components/search/utils/voucherExtra.ts';
import { getDealCardMyPriceDiscountPercentage } from '@/app/components/search/components/Card/utils/getPriceDetails.ts';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import TourOperatorLogoBadge from '../Details/Badges/TourOperatorLogoBadge';

const PHONE_ICON: StarfishIconRef = {
  category: 'UI Essentials',
  name: 'icon-phone',
  color: 'iconOnDark',
  inkScale: 1.2,
};

export type PromotionProps = {
  promotionTypes: PROMOTION_TYPES[];
  localization: Localization;
  t: TranslationFunction;
  isMobile: boolean;
  totalSavingsPercentageLabel: string | null;
  callbacks: {
    [key: string]: {
      callback: () => void;
    };
  };
  dealCard: DealCard;
};

export const getPricePromotions = ({
  promotionTypes,
  localization,
  t,
  isMobile,
  totalSavingsPercentageLabel,
  callbacks,
  dealCard,
}: PromotionProps): PromotionConfig[] => {
  const promotions: PromotionConfig[] = [];
  const { imageBasePath, brand, country, currency } = localization;
  const hasPromotionTypes = promotionTypes && promotionTypes.length > 0;
  const packageSavingsUspEnabled = localization?.configValuesApp?.packageSavingsUspEnabled;
  const lastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals;

  if (hasPromotionTypes) {
    // Check if periodic promotions are already in the array
    const hasPeriodicPromotion =
      promotionTypes.includes(PROMOTION_TYPES.BLACK_FRIDAY) ||
      promotionTypes.includes(PROMOTION_TYPES.CYBER_MONDAY) ||
      promotionTypes.includes(PROMOTION_TYPES.TRAVEL_TUESDAY) ||
      promotionTypes.includes(PROMOTION_TYPES.SUMMER_SALE);

    promotionTypes.forEach((promotionType) => {
      if (promotionType === PROMOTION_TYPES.FLASH_SALES) {
        if (lastMinuteDeals) {
          promotions.push({
            promotionType: PROMOTION_TYPES.FLASH_SALES,
            icon: `${imageBasePath}icons/last_minute_deals_rebrand.svg`,
            Element: () => <LastMinuteDealsBadge skipPeriodicPromotion={hasPeriodicPromotion} />,
          });
        } else {
          promotions.push({
            text: t('vacation.oss.cards.flash_sales_label'),
            promotionType: PROMOTION_TYPES.FLASH_SALES,
            icon: { category: 'Brands and products', name: 'icon-flash-sales', color: 'iconOnDark' } as const,
          });
        }
      }

      if (promotionType === PROMOTION_TYPES.PINK_PEOPLE) {
        promotions.push({
          text: t('vacation.oss.cards.pinkpeople_label_html'),
          promotionType: PROMOTION_TYPES.PINK_PEOPLE,
          icon: `${imageBasePath}icons/pinkpeople.svg`,
        });
      }

      if (promotionType === PROMOTION_TYPES.APP_ONLY_PRICE) {
        promotions.push({
          icon: PHONE_ICON,
          text: t('vacation.oss.cards.app_only_price'),
          promotionType: PROMOTION_TYPES.APP_ONLY_PRICE,
        });
      }

      if (promotionType === PROMOTION_TYPES.MOBILE_ONLY_PRICE) {
        promotions.push({
          icon: PHONE_ICON,
          text: t('vacation.oss.cards.mobile_only_price'),
          promotionType: PROMOTION_TYPES.MOBILE_ONLY_PRICE,
        });
      }

      if (promotionType === PROMOTION_TYPES.TOP_SECRET_HOTELS) {
        promotions.push({
          text: t('vacation.oss.cards.tsh_label_html'),
          icon: `${imageBasePath}icons/tsh_card.svg`,
          promotionType: PROMOTION_TYPES.TOP_SECRET_HOTELS,
        });
      }

      if (promotionType === PROMOTION_TYPES.MY_PRICE) {
        if (isMobile) {
          const discountPercentage = getDealCardMyPriceDiscountPercentage(dealCard, true);

          if (discountPercentage) {
            promotions.push({
              text: discountPercentage,
              className: 'my-price-percentage',
              promotionType: PROMOTION_TYPES.MY_PRICE,
            });
          }
        }
        promotions.push({
          text: t('vacation.oss.cards.myprice_label_html'),
          icon: `${imageBasePath}icons/my_price_white.svg`,
          promotionType: PROMOTION_TYPES.MY_PRICE,
        });
      }

      const periodicLabelKey = PERIODIC_PROMOTION_LABEL_KEYS[promotionType];
      if (periodicLabelKey) {
        promotions.push({
          text: t(periodicLabelKey),
          promotionType,
        });
      }

      if (promotionType === PROMOTION_TYPES.JANUARY_SALE) {
        promotions.push({
          text: t('vacation.offer.january_sale_offer'),
          promotionType: PROMOTION_TYPES.JANUARY_SALE,
        });
      }

      if (promotionType === PROMOTION_TYPES.TO_WEGDE) {
        promotions.push({
          text: t('vacation.oss.cards.weg_de_label'),
          promotionType: PROMOTION_TYPES.TO_WEGDE,
        });
      }

      if (promotionType === PROMOTION_TYPES.TO_LOGO) {
        promotions.push({
          Element: () => <TourOperatorLogoBadge code={dealCard.product.accommodation.toCode} />,
          promotionType: PROMOTION_TYPES.TO_LOGO,
          className: 'tour-operator-logo',
        });
      }

      if (promotionType === PROMOTION_TYPES.TO_LASTMINUTE) {
        promotions.push({
          text: t('vacation.oss.cards.lastminute_de_label'),
          promotionType: PROMOTION_TYPES.TO_LASTMINUTE,
        });
      }

      if (
        promotionType === PROMOTION_TYPES.PACKAGE_SAVINGS &&
        totalSavingsPercentageLabel &&
        !packageSavingsUspEnabled
      ) {
        promotions.push({
          icon: { category: 'Semantics', name: 'icon-circle-info', color: 'iconOnDark' } as const,
          text: totalSavingsPercentageLabel,
          promotionType: PROMOTION_TYPES.PACKAGE_SAVINGS,
          callback: callbacks[PROMOTION_TYPES.PACKAGE_SAVINGS].callback,
        });
      }

      if (
        voucherExtraBannerSupported(VOUCHER_EXTRA_TYPE.THIRTY, brand, country, currency) && // TODO: only 30 is currently supported
        promotionType === PROMOTION_TYPES.VOUCHER_EXTRA
      ) {
        const voucherExtraValues = getVoucherExtraPromotionValues(localization);
        const voucherCode = voucherExtraValues?.voucherCode;
        const discountAmount = voucherExtraValues?.discountAmount;

        const text = replacePlaceholdersWithValues(t('vacation.oss.cards.voucher_extra_label'), {
          discountAmount: `${discountAmount}</br>`,
          voucherCode: `<b>${voucherCode}</b>`,
        });

        promotions.push({
          text,
          icon: { category: 'Financial', name: 'icon-discount', color: 'iconPrimaryDefault' } as const,
          promotionType,
        });
      }
    });
  }

  return isMobile ? promotions.reverse() : promotions;
};
